import { useEffect, useMemo } from "react";
import { Link } from "wouter";
import { ArrowRight, BookOpen, CheckCircle2, Clock, Lock, PlayCircle, ShieldCheck, Sparkles, Wrench } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Badge, Card, PrimaryButton, SectionTitle } from "@/components/Card";
import { useDemoStore } from "@/store/useDemoStore";
import {
  useCandidatesList,
  useEffectiveUser,
  useEnrollmentsForCandidate,
  useExpedienteForCandidateStandard,
  useLmsAvancePct,
  useLmsProgress,
  useLmsRoute,
} from "@/store/hooks";
import type { LmsModule } from "@/data/seed";

const ROUTE_READY_PCT = 80;

export default function CandidateRuta() {
  const setRole = useDemoStore((s) => s.setRole);
  useEffect(() => setRole("alumno"), [setRole]);

  const effective = useEffectiveUser();
  const candList = useCandidatesList();
  const candidateId = effective && effective.roles.includes("alumno") ? effective.id : candList[0]?.id;
  const enrollments = useEnrollmentsForCandidate(candidateId);
  const codigo = enrollments.find((c) => c === "EC1781") ?? enrollments[0];
  const exp = useExpedienteForCandidateStandard(candidateId, codigo);
  const route = useLmsRoute(codigo);
  const progress = useLmsProgress(exp?.id);
  const avancePct = useLmsAvancePct(exp?.id, codigo);

  const completedSet = useMemo(() => new Set(progress.completedModuleIds), [progress.completedModuleIds]);

  const isLocked = (mod: LmsModule, idx: number) => {
    if (!route) return true;
    if (idx === 0) return false;
    const prev = route.modules[idx - 1];
    return !completedSet.has(prev.id);
  };

  const nextModule = useMemo(() => {
    if (!route) return undefined;
    return route.modules.find((m) => !completedSet.has(m.id));
  }, [route, completedSet]);

  if (!exp || !route || codigo !== "EC1781") {
    return (
      <AppLayout pageTitle="Mi ruta formativa">
        <Card>
          <div style={{ padding: 24, textAlign: "center", color: "var(--cc-gray-600)" }}>
            La ruta formativa interactiva está disponible para el estándar EC1781. Si está inscrito en otro estándar, su instructor le compartirá los materiales correspondientes.
          </div>
        </Card>
      </AppLayout>
    );
  }

  return (
    <AppLayout pageTitle="Mi ruta formativa EC1781">
      <Card style={{ background: "linear-gradient(135deg, var(--cc-navy) 0%, #243259 100%)", color: "white", border: "none", marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.6)", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 4 }}>
              RUTA FORMATIVA INTERACTIVA
            </div>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: "white", marginBottom: 6 }} data-testid="text-ruta-title">
              EC1781 — Conducción de motocicleta en seguridad pública
            </h1>
            <div style={{ fontSize: 13, color: "rgba(255,255,255,0.75)", lineHeight: 1.5 }}>
              {route.blocks.length} bloques · {route.modules.length} módulos · ~{route.modules.reduce((a, m) => a + m.duracionMin, 0)} min
            </div>
            <div style={{ marginTop: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, fontSize: 12 }}>
                <span>Avance global</span>
                <span style={{ fontWeight: 700 }} data-testid="text-avance-pct">{avancePct}%</span>
              </div>
              <div style={{ background: "rgba(255,255,255,0.15)", borderRadius: 999, height: 8, overflow: "hidden" }}>
                <div style={{ background: avancePct >= ROUTE_READY_PCT ? "var(--cc-green)" : "var(--cc-teal-light)", width: `${avancePct}%`, height: "100%", transition: "width 0.25s" }} />
              </div>
            </div>
          </div>
          <div style={{ minWidth: 200, textAlign: "right" }}>
            {avancePct >= ROUTE_READY_PCT ? (
              <Badge tone="green">✓ Listo para evaluación</Badge>
            ) : (
              <Badge tone="amber">En progreso · Faltan {ROUTE_READY_PCT - avancePct}% para evaluación</Badge>
            )}
            {nextModule && (
              <div style={{ marginTop: 12 }}>
                <Link href={nextModule.kind === "simulador-preop" ? `/alumno/ruta/simulador/${nextModule.id}` : `/alumno/ruta/modulo/${nextModule.id}`} data-testid="link-continue-route">
                  <PrimaryButton variant="dark">
                    <PlayCircle size={14} /> Continuar: {nextModule.titulo.slice(0, 28)}{nextModule.titulo.length > 28 ? "…" : ""} <ArrowRight size={12} />
                  </PrimaryButton>
                </Link>
              </div>
            )}
          </div>
        </div>
      </Card>

      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {route.blocks.map((block) => {
          const modules = route.modules.filter((m) => m.blockId === block.id);
          const blockDone = modules.length > 0 && modules.every((m) => completedSet.has(m.id));
          return (
            <Card key={block.id} testId={`block-${block.id}`}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, paddingBottom: 8, borderBottom: "1px solid var(--cc-gray-100)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: blockDone ? "var(--cc-green-bg)" : "var(--cc-teal-bg)", color: blockDone ? "var(--cc-green)" : "var(--cc-teal)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 }}>
                    {block.orden}
                  </div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "var(--cc-navy)" }}>{block.titulo}</div>
                    <div style={{ fontSize: 11, color: "var(--cc-gray-500)" }}>{block.descripcion}</div>
                  </div>
                </div>
                {blockDone ? <Badge tone="green">Bloque completo</Badge> : <Badge tone="gray">{modules.filter((m) => completedSet.has(m.id)).length}/{modules.length}</Badge>}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {modules.map((mod) => {
                  const moduleIdx = route.modules.findIndex((m) => m.id === mod.id);
                  const locked = isLocked(mod, moduleIdx);
                  const done = completedSet.has(mod.id);
                  const isPreop = mod.kind === "simulador-preop";
                  const href = isPreop
                    ? `/alumno/ruta/simulador/${mod.id}`
                    : `/alumno/ruta/modulo/${mod.id}`;
                  const KindIcon = isPreop ? Wrench : mod.kind === "evaluacion-final" ? ShieldCheck : BookOpen;
                  return (
                    <div
                      key={mod.id}
                      data-testid={`module-row-${mod.id}`}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        padding: "10px 12px",
                        background: done ? "rgba(34,197,94,0.06)" : locked ? "var(--cc-gray-50, #fafbfc)" : "white",
                        border: `1px solid ${done ? "rgba(34,197,94,0.2)" : "var(--cc-gray-100)"}`,
                        borderRadius: 8,
                        opacity: locked ? 0.6 : 1,
                      }}
                    >
                      <div style={{ width: 28, height: 28, borderRadius: 6, background: done ? "var(--cc-green-bg)" : "var(--cc-gray-100)", color: done ? "var(--cc-green)" : "var(--cc-gray-600)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {done ? <CheckCircle2 size={16} /> : locked ? <Lock size={14} /> : <KindIcon size={14} />}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--cc-navy)" }}>{mod.titulo}</div>
                        <div style={{ fontSize: 11, color: "var(--cc-gray-500)", display: "flex", alignItems: "center", gap: 8, marginTop: 2 }}>
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}><Clock size={10} /> {mod.duracionMin} min</span>
                          {isPreop && <Badge tone="navy">Simulador</Badge>}
                          {mod.kind === "evaluacion-final" && <Badge tone="teal">Evaluación final</Badge>}
                          {mod.criteriosCubiertos && mod.criteriosCubiertos.length > 0 && (
                            <span style={{ color: "var(--cc-gray-400)" }}>Cubre: {mod.criteriosCubiertos.join(", ")}</span>
                          )}
                        </div>
                      </div>
                      <div>
                        {locked ? (
                          <Badge tone="gray">Bloqueado</Badge>
                        ) : (
                          <Link href={href} data-testid={`link-module-${mod.id}`}>
                            <PrimaryButton size="sm" variant={done ? "outline" : "primary"}>
                              {done ? "Repasar" : "Iniciar"} <ArrowRight size={11} />
                            </PrimaryButton>
                          </Link>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          );
        })}
      </div>

      <Card style={{ marginTop: 16, background: "var(--cc-teal-bg)", border: "1px solid rgba(20,184,166,0.2)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Sparkles size={18} color="var(--cc-teal)" />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--cc-navy)" }}>
              ¿Cómo se desbloquea cada módulo?
            </div>
            <div style={{ fontSize: 12, color: "var(--cc-gray-700)", marginTop: 2 }}>
              Cada lección termina con un quiz de 3 preguntas. Necesitas al menos <strong>2 aciertos</strong> para avanzar. Al alcanzar <strong>{ROUTE_READY_PCT}% de la ruta</strong>, tu expediente se marca como “Listo para evaluación”.
            </div>
          </div>
        </div>
      </Card>
    </AppLayout>
  );
}
